import { Box, Divider, Typography, TypographyProps } from "@mui/material";
import AttributeSvg from "../../../svg/AttributeSvg";
import { ViewBlockHeader } from "./ViewBlockHeader";
import useNodeContext from "../../../hooks/useNodeContext";
import { isDefined } from "@convin/utils/helper/common.helper";
import { AttributeBlockFormType } from "../../../../types/bert";
import SkeletonLoader from "@convin/components/custom_components/Loader/SkeletonLoader";

import BlockItem from "./BlockItem";
import FlexItemsWithOverflow from "./ItemsWithOverflow";
import { attribute_types } from "../../../constants";
import { useAttributeData } from "./useAttributeData";

// Types
type LevelProps = TypographyProps & {
    label: string;
    value: string | null;
};

// Components
export const Level = ({
    label,
    value,
    variant = "textSm",
    fontWeight = 500,
}: LevelProps) => {
    if (!value) return null;
    return (
        <div className="flex gap-1">
            <Typography
                color="textColors.666"
                variant={variant}
                fontWeight={fontWeight}
            >
                {label}:
            </Typography>
            <Typography
                variant={variant}
                fontWeight={fontWeight}
                color="textColors.333"
            >
                {value}
            </Typography>
        </div>
    );
};

// Hooks

// Main Component
export const Attribute = () => {
    const { nodeDataToUpdate } = useNodeContext() as {
        nodeDataToUpdate: AttributeBlockFormType;
    };

    const {
        meetingTypeOptions,
        attribute_type,
        callTypes,
        callTags,
        isTypesLoading,
        isTagsLoading,
        isEntitiesLoading,
        entityValue,
        dispositionTemplateValue,
        level1Value,
        level2Value,
        level3Value,
    } = useAttributeData(nodeDataToUpdate);

    const attribute = isDefined(nodeDataToUpdate)
        ? { ...nodeDataToUpdate }
        : {
              name: "",
              attribute_type: "",
              attribute: [] as string[],
          };

    if (isTypesLoading || isTagsLoading || isEntitiesLoading) {
        return (
            <div>
                <ViewBlockHeader icon={<AttributeSvg />} title="Attribute" />
                <SkeletonLoader />
            </div>
        );
    }

    const attributeOptions = (
        attribute_type
            ? attribute_type === "conversation_type"
                ? meetingTypeOptions
                : attribute_type === "tags"
                ? callTags ?? []
                : callTypes ?? []
            : []
    ).map((option) => ({
        id: option.id,
        label: option.label,
    }));

    const selectedAttributes = attributeOptions.filter((option) =>
        attribute?.attribute?.includes(option.id as string)
    );
    return (
        <div className="flex flex-col gap-2">
            <ViewBlockHeader icon={<AttributeSvg />} title="Attribute" />
            <Box className="flex flex-col" sx={{ p: 2, gap: 1.5 }}>
                <BlockItem title="Block Name" value={attribute.name} />
                <Divider />
                <BlockItem
                    title="Attribute Type"
                    value={
                        attribute_types.find(
                            (type) => type.id === attribute.attribute_type
                        )?.label
                    }
                />
                {(attribute_type === "ai_disposition" ||
                    attribute_type === "entity") && (
                    <>
                        <Divider />
                        <BlockItem
                            title={
                                attribute_type === "ai_disposition"
                                    ? "Disposition Template"
                                    : "Entity"
                            }
                            value={
                                attribute_type === "ai_disposition"
                                    ? dispositionTemplateValue
                                    : entityValue
                            }
                        />
                    </>
                )}
                {attribute_type === "ai_disposition" && (
                    <>
                        <Divider />
                        <BlockItem
                            title="Levels"
                            value=""
                            Component={
                                <div className="flex flex-col gap-1">
                                    <Level label="L1" value={level1Value} />
                                    <Level label="L2" value={level2Value} />
                                    <Level label="L3" value={level3Value} />
                                </div>
                            }
                        />
                    </>
                )}
                {attribute_type !== "ai_disposition" && (
                    <>
                        <Divider />
                        <BlockItem
                            title={
                                attribute_type === "entity"
                                    ? "List of Eligible Values"
                                    : "Selected Attribute"
                            }
                            value=""
                            Component={
                                <FlexItemsWithOverflow
                                    drawerTitle="Selected Attributes"
                                    items={
                                        attribute_type === "entity"
                                            ? nodeDataToUpdate.eligible_entities ??
                                              []
                                            : selectedAttributes.map(
                                                  (option) => option.label
                                              )
                                    }
                                />
                            }
                        />
                    </>
                )}
            </Box>
        </div>
    );
};

export default Attribute;
